import '../../../../../scripts/typings/utils.d';
import ActionBar from '../../../common/components/documentViewer/actionBar';
import CustomActionButton from '../../../common/components/documentViewer/customActionButton';
import DocumentTitle from '../../../common/components/documentViewer/documentTitle';
import HeaderContainer from '../../../common/components/documentViewer/headerContainer';
import TitleAndActionsContainer from '../../../common/components/documentViewer/titleAndActionsContainer';
import { ICustomAction } from '../../../common/typings/DocumentViewer/CommonViewerViewModel';
import { SupplementalMaterialsStatus } from '../../../common/typings/DocumentViewer/enums';
import { commonTestIds } from '../../../common/utils/commonTestIds';
import { OrderDocumentOptions, OrderViewerOptions } from '../typing/enums';
import { IOrderViewerMainStore } from '../typing/stores';
import { NonAnonymousActions } from '../utils/constants';
import { isOrderDrm } from '../utils/orderUtils';
import AddToSharedLibraryButton from './orderViewerActions/addToSharedLibrary';
import ContactUsButton from './orderViewerActions/contactUsButton';
import EmailCitationButton from './orderViewerActions/emailCitationButton';
import EnrichButton from './orderViewerActions/enrichDocument';
import PopOutButton from './orderViewerActions/popOutButton';
import PrintReceipt from './orderViewerActions/printReceipt';
import PublisherSiteButton from './orderViewerActions/publisherSiteButton';
import RequestNewVersionButton from './orderViewerActions/requestNewVersionButton';
import RightsInfoButton from './orderViewerActions/rightsInfoButton';
import SupplementalMaterialsButton from './orderViewerActions/supplementalMaterialsButton';
import ViewInLibraryButton from './orderViewerActions/viewInLibraryButton';
import RequestInfo from './requestInfo';
import { createStyles, makeStyles } from '@mui/styles';
import { inject, observer } from 'mobx-react';
import { useEffect, useRef } from 'react';

export interface IViewerHeaderProps {
	title?: string;
	publisherSite?: string;
	requestNumber?: string;
	options?: OrderViewerOptions;
	supplementalMaterialsStatus?: SupplementalMaterialsStatus;
	customAction?: ICustomAction;
	deliveryLink?: string;
	documentOptions?: OrderDocumentOptions;
	methodAfterRender?: string;
}

enum OrderViewerAction {
	RightsInfo = 1,
	PublisherSite = 2,
	CustomAction = 3,
	SupplementalData = 5,
	EnrichDocument = 6,
	AddToSharedLibrary = 7,
	EmailCitation = 8,
	PopOut = 9,
	RequestNewVersion = 10,
	OrderReceipt = 11,
	ContactUs = 12
}

const useStyles = makeStyles(() =>
	createStyles({
		hiddenButton: {
			display: 'none'
		}
	})
);

const ViewerHeader: React.FunctionComponent<IViewerHeaderProps> = props => {
	const primaryActionsCount = 3;
	const inputRef = useRef(null);

	useEffect(() => {
		inputRef?.current?.click();
	}, []);

	const classes = useStyles();

	const displayAction = (action: OrderViewerAction): boolean => {
		switch (action) {
			case OrderViewerAction.PublisherSite: {
				return props.publisherSite != null;
			}
			case OrderViewerAction.CustomAction: {
				return props.customAction != null;
			}
			case OrderViewerAction.SupplementalData: {
				return props.supplementalMaterialsStatus !== SupplementalMaterialsStatus.None;
			}
			case OrderViewerAction.EnrichDocument: {
				return (props.options & OrderViewerOptions.Enrichment) === OrderViewerOptions.Enrichment;
			}
			case OrderViewerAction.AddToSharedLibrary: {
				return (props.options & OrderViewerOptions.AddToSharedLib) === OrderViewerOptions.AddToSharedLib;
			}
			case OrderViewerAction.RequestNewVersion: {
				return (props.options & OrderViewerOptions.RequestNewVersion) === OrderViewerOptions.RequestNewVersion;
			}
			case OrderViewerAction.OrderReceipt: {
				return (props.options & OrderViewerOptions.ShowReceipt) === OrderViewerOptions.ShowReceipt;
			}
			case OrderViewerAction.PopOut: {
				return (
					props.deliveryLink &&
					!isOrderDrm(props.documentOptions) &&
					(props.documentOptions & OrderDocumentOptions.LinkOut) !== OrderDocumentOptions.LinkOut
				);
			}
		}

		return true;
	};

	const getActionButton = (action: OrderViewerAction, isPrimary: boolean, key: number): React.ReactElement => {
		const actionProps = {
			isPrimary,
			key,
			controlledBy: !isPrimary ? commonTestIds.actionBarDropdown : ''
		};

		switch (action) {
			case OrderViewerAction.RightsInfo: {
				return <RightsInfoButton {...actionProps} />;
			}
			case OrderViewerAction.PublisherSite: {
				return <PublisherSiteButton {...actionProps} />;
			}
			case OrderViewerAction.CustomAction: {
				return <CustomActionButton {...props.customAction} {...actionProps} />;
			}
			case OrderViewerAction.SupplementalData: {
				return <SupplementalMaterialsButton {...actionProps} />;
			}
			case OrderViewerAction.EnrichDocument: {
				return <EnrichButton {...actionProps} />;
			}
			case OrderViewerAction.AddToSharedLibrary: {
				return <AddToSharedLibraryButton {...actionProps} />;
			}
			case OrderViewerAction.EmailCitation: {
				return <EmailCitationButton {...actionProps} />;
			}
			case OrderViewerAction.PopOut: {
				return <PopOutButton {...actionProps} />;
			}
			case OrderViewerAction.RequestNewVersion: {
				return <RequestNewVersionButton {...actionProps} />;
			}
			case OrderViewerAction.OrderReceipt: {
				return <PrintReceipt {...actionProps} />;
			}
			case OrderViewerAction.ContactUs: {
				return <ContactUsButton {...actionProps} />;
			}
		}
	};

	const getActionAfterRender = () => {
		const actionProps = {
			isPrimary: true,
			key: -1,
			className: classes.hiddenButton
		};

		// we are rendering a hidden button because we need to call the button handler after login
		// we can't call the handler from the button from the secondary menu because
		// we can't extract handlers from buttons because then we'd have to copy the properties injection
		// rendering a hidden button is the lesser evil
		switch (props.methodAfterRender) {
			case NonAnonymousActions.AddToSharedLibrary: {
				return displayAction(OrderViewerAction.AddToSharedLibrary) ? (
					<AddToSharedLibraryButton {...actionProps} ref={inputRef} />
				) : null;
			}
			case NonAnonymousActions.RequestNewVersion: {
				return displayAction(OrderViewerAction.RequestNewVersion) ? (
					<RequestNewVersionButton {...actionProps} ref={inputRef} />
				) : null;
			}
			case NonAnonymousActions.RightsInfo: {
				return displayAction(OrderViewerAction.RightsInfo) ? (
					<RightsInfoButton {...actionProps} ref={inputRef} />
				) : null;
			}
			case NonAnonymousActions.SupplementalRequest: {
				return displayAction(OrderViewerAction.SupplementalData) ? (
					<SupplementalMaterialsButton {...actionProps} ref={inputRef} />
				) : null;
			}
		}

		return null;
	};

	const buttons = Object.values(OrderViewerAction)
		.filter(val => typeof val === 'number')
		.filter(displayAction)
		.map((action, index) => getActionButton(action as OrderViewerAction, index < primaryActionsCount, index));

	// ViewInLibrary always in the last position of primary menu
	const viewInLibrary = <ViewInLibraryButton key={primaryActionsCount} isPrimary={true} />;
	const buttonsLimit = Math.min(buttons.length, primaryActionsCount);
	buttons.splice(buttonsLimit, 0, viewInLibrary);

	return (
		<HeaderContainer>
			<TitleAndActionsContainer>
				<DocumentTitle title={props.title} />
				{props.methodAfterRender && getActionAfterRender()}
				<ActionBar visibleActionsCount={primaryActionsCount + 1}>{buttons}</ActionBar>
			</TitleAndActionsContainer>
			<RequestInfo requestNumber={props.requestNumber} />
		</HeaderContainer>
	);
};

export default inject((stores: IOrderViewerMainStore) => ({
	customAction: stores.orderViewerStore.viewModel.headerModel.customAction,
	title: stores.orderViewerStore.viewModel.documentModel.title,
	publisherSite: stores.orderViewerStore.viewModel.headerModel.publisherSite,
	requestNumber: stores.orderViewerStore.viewModel.headerModel.requestNumber,
	options: stores.orderViewerStore.viewModel.viewerOptions,
	supplementalMaterialsStatus:
		stores.orderViewerStore.viewModel.headerModel.supplementalMaterialViewModel.supplementalMaterialsStatus,
	deliveryLink: stores.orderViewerStore.viewModel.documentModel.deliveryLink,
	documentOptions: stores.orderViewerStore.viewModel.documentModel.documentOptions,
	methodAfterRender: stores.orderViewerStore.viewModel.methodAfterRender
}))(observer(ViewerHeader));
